/**
 * Parses a string to extract a numerical value and its unit.
 *
 * @param input - The input string containing a number and a unit.
 * @returns An object with the parsed value and unit. If parsing fails, returns { value: null, unit: "" }.
 *
 * @example
 * parseNumberAndUnit("12.5 kg") // { value: 12.5, unit: "kg" }
 * parseNumberAndUnit("100m") // { value: 100, unit: "m" }
 */
const parseNumberAndUnit = (
  input: string
): { value: number | null; unit: string } => {
  if (!input || typeof input !== 'string') return { value: null, unit: "" }
  const match = input.match(/^([\d.,]+)\s*(.*)$/)
  if (!match) return { value: null, unit: "" }

  const value = parseFloat(match[1].replace(",", "."))
  const unit = match[2].trim()

  return { value, unit }
}

/**
 * Finds the maximum values for each unit from an array of input strings.
 *
 * @param inputs - An array of strings, each containing a number and a unit.
 * @returns An array of strings representing the maximum values for each unit.
 *
 * @example
 * getMaxValuesByUnit(["12.5 kg", "15 kg", "100m", "150m"]) // ["15kg", "150m"]
 */
export const getMaxValuesByUnit = (inputs: string[]): string[] => {
  if (!inputs || !Array.isArray(inputs)) return []
  
  const parsedValues = inputs
    .filter((input) => input && typeof input === 'string')
    .map((input) => parseNumberAndUnit(input))
    .filter((entry) => entry.value !== null) as {
    value: number
    unit: string
  }[]

  const maxValues = new Map<string, { value: number; original: string }>()

  for (const { value, unit } of parsedValues) {
    if (!maxValues.has(unit) || value > maxValues.get(unit)!.value) {
      maxValues.set(unit, { value, original: `${value}${unit}` })
    }
  }

  return Array.from(maxValues.values()).map((entry) => entry.original)
}
